/**
 * Authenticated app layout. Grouped sidebar nav + an outlet for the active page.
 *
 * Nav is grouped into a short story (Overview → Marketing & Sales → Customers →
 * Operations → Admin) instead of one flat list. "Get set up" only takes a top
 * slot while setup is incomplete; once done it drops to a muted Admin entry so
 * it stops squatting the #1 position. The browser tab title tracks the page.
 */

import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigationType } from "react-router-dom";
import { UserButton, useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart3, Megaphone, Package, Users, Repeat, RefreshCw, Wrench, Plug,
  Settings as SettingsIcon, Rocket, ChevronsUpDown, Check, PanelLeftClose, PanelLeftOpen, Bug, Lightbulb, Briefcase, Landmark, Receipt, type LucideIcon,
} from "lucide-react";
import { useApiClient } from "../lib/api-context.js";
import { FeedbackModal } from "../components/FeedbackModal.js";
import { DrillProvider } from "../lib/drill.js";
import { ToastProvider } from "../lib/toast.js";
import { PeriodProvider, usePeriod, PERIODS } from "../lib/period.js";
import { Segmented } from "../components/Segmented.js";
import { CustomRangePicker } from "../components/CustomRangePicker.js";
import { CustomizePanel } from "../components/CustomizePanel.js";
import { HideProvider, CustomizeBar } from "../lib/hide.js";
import { CardApprovalProvider } from "../lib/card-approval.js";
import { useCardSpotlight } from "../lib/spotlight.js";
import { ErrorBoundary } from "../components/ErrorBoundary.js";
import { CostReadinessBanner } from "../lib/cost-readiness-banner.js";
import { VerificationReadinessBanner } from "../lib/verification-readiness-banner.js";
import { DataTrustBanner } from "../lib/data-trust-banner.js";
import { GrossResultsNotice, shouldShowGrossResultsNotice } from "../lib/gross-results-notice.js";
import { OperatorCostNotice } from "../lib/operator-cost-notice.js";
import { DashboardFirstInsightTour } from "../lib/dashboard-first-insight-tour.js";
import { NeedsBanner } from "../lib/needs-banner.js";
import { VersionWatcher } from "../lib/version-watch.js";
import { SyncPill } from "../lib/sync-pill.js";
import { DisplayOptsProvider } from "../lib/display-opts.js";
import { PageFiltersProvider, PageFilterRail, pageHasProductFilter } from "../lib/page-filters.js";
import { useViewportWidth, BREAK } from "../lib/use-viewport.js";
import { useTheme } from "../lib/theme.js";
import { operatorSetupNotice, type SetupStep } from "../lib/account-setup-routing.js";
import type { Capabilities } from "../lib/card-requirements.js";
import { useAccountChange, useRequiredActiveAccountId } from "../lib/account-context.js";

/** Global date-range control (research: one period control at the top, presets
 *  + the resolved dates visible). Lives in its own component so it can call
 *  usePeriod() from inside the provider. */
function PeriodBar(): React.ReactElement {
  const period = usePeriod();
  return (
    <div data-onboarding-tour="period" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <Segmented
        ariaLabel="Time period"
        options={PERIODS.map((p) => ({ value: p.value, label: p.short }))}
        // "" when a custom range is active → no pill highlighted (the calendar is).
        value={(period.key === "custom" ? "" : period.key) as (typeof PERIODS)[number]["value"]}
        onChange={period.setKey}
      />
      <CustomRangePicker
        active={period.key === "custom"}
        activeLabel={period.label}
        from={period.from}
        to={period.to}
        onApply={period.setCustom}
      />
      <span className="dd-num" style={{ color: "var(--text-faint)", fontSize: 12 }}>{period.rangeHint}</span>
    </div>
  );
}

interface OnboardingState {
  completed: boolean;
  previewAllowed: boolean;
  steps: SetupStep[];
}

interface Membership {
  accountId: string;
  role: string;
  name: string;
  logoUrl: string | null;
  themeColorHex: string | null;
  isActive: boolean;
}

export function singleAccountSubBrand(accountName: string): string {
  return accountName.trim() || "Your account";
}

/**
 * Account switcher (agency model — one login, several client brands). Renders
 * as the client's account name when the user only has one account; becomes a
 * dropdown the moment a second membership exists.
 */
export function AccountSwitcher({ homePath = "/dashboard" }: { homePath?: "/dashboard" | "/cohorts" }): React.ReactElement {
  const api = useApiClient();
  const changeAccount = useAccountChange();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const { data, isSuccess } = useQuery({
    queryKey: ["account", "memberships"],
    queryFn: () => api.get<{ memberships: Membership[] }>("/account/memberships"),
    staleTime: 5 * 60_000,
  });
  // Current account name — a fallback for the trigger when memberships hasn't
  // loaded yet (or errored). Cached by the layout, so no extra request.
  const acctQ = useQuery({
    queryKey: ["account"],
    queryFn: () => api.get<{ account: { name?: string } }>("/account"),
    staleTime: 5 * 60_000,
  });
  const memberships = data?.memberships ?? [];
  const active = memberships.find((m) => m.isActive);
  const activeName = active?.name ?? acctQ.data?.account?.name ?? "Your account";

  // Collapse to the single-brand label ONLY when the query genuinely succeeded
  // with a single account. While LOADING or on ERROR, keep the switcher visible
  // (showing the current account) — a transient memberships failure was making
  // the client chooser vanish on one tab but not the other (founder 2026-07-07).
  if (isSuccess && memberships.length < 2) {
    return <div style={layoutStyles.subBrand}>{singleAccountSubBrand(activeName)}</div>;
  }

  const switchTo = async (accountId: string): Promise<void> => {
    if (accountId === active?.accountId || switching !== null) {
      setOpen(false);
      return;
    }
    setSwitching(accountId);
    try {
      await changeAccount({
        path: "/account/switch",
        body: { accountId },
        targetAccountId: accountId,
        destination: homePath,
      });
    } catch {
      setSwitching(null);
      setOpen(false);
    }
  };

  return (
    <div style={{ position: "relative", marginTop: 6, marginLeft: 27 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={switcherStyles.trigger}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {activeName}
        </span>
        <ChevronsUpDown size={13} strokeWidth={2} style={{ flexShrink: 0, opacity: 0.7 }} />
      </button>
      {open && (
        <>
          <div style={switcherStyles.overlay} onClick={() => setOpen(false)} />
          <div role="listbox" style={switcherStyles.menu}>
            <div style={switcherStyles.menuLabel}>Your accounts</div>
            {memberships.map((m) => (
              <button
                key={m.accountId}
                type="button"
                role="option"
                aria-selected={m.isActive}
                onClick={() => void switchTo(m.accountId)}
                style={{
                  ...switcherStyles.item,
                  ...(m.isActive ? switcherStyles.itemActive : {}),
                  opacity: switching !== null && switching !== m.accountId ? 0.5 : 1,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{ ...switcherStyles.swatch, background: m.themeColorHex ?? "var(--accent)" }}
                />
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" }}>
                  {switching === m.accountId ? "Switching…" : m.name}
                </span>
                {m.isActive && <Check size={14} strokeWidth={2.5} style={{ color: "var(--accent)", flexShrink: 0 }} />}
              </button>
            ))}
            {memberships.length === 0 && (
              <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--body)" }}>
                Couldn’t load your accounts just now — <button type="button" onClick={() => window.location.reload()} style={{ background: "none", border: "none", color: "var(--accent)", cursor: "pointer", padding: 0, font: "inherit", textDecoration: "underline" }}>refresh</button>.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

interface NavItem { label: string; to: string; icon: LucideIcon }
interface NavGroup { label: string | null; items: NavItem[] }

const NAV_GROUPS: NavGroup[] = [
  { label: null, items: [{ label: "Exec", to: "/dashboard", icon: BarChart3 }] },
  {
    label: "Marketing & Sales",
    items: [
      { label: "Marketing", to: "/marketing", icon: Megaphone },
      { label: "Products", to: "/products", icon: Package },
    ],
  },
  {
    label: "Customers",
    items: [
      { label: "Customer LTV", to: "/cohorts", icon: Users },
      { label: "Retention", to: "/retention", icon: Repeat },
      { label: "Subscriptions", to: "/subscriptions", icon: RefreshCw },
    ],
  },
  {
    label: "Operations",
    items: [
      { label: "Operations", to: "/operations", icon: Wrench },
      { label: "Financials", to: "/financials", icon: Landmark },
    ],
  },
  {
    label: "Admin",
    items: [
      { label: "Connections", to: "/connections", icon: Plug },
      { label: "Costs", to: "/costs", icon: Receipt },
      { label: "Settings", to: "/settings", icon: SettingsIcon },
    ],
  },
];

const CLIENT_NAV_GROUPS: NavGroup[] = [
  { label: null, items: [
    { label: "Customer LTV", to: "/cohorts", icon: Users },
    { label: "Exec", to: "/dashboard", icon: BarChart3 },
  ] },
  {
    label: "Marketing & Sales",
    items: [
      { label: "Marketing", to: "/marketing", icon: Megaphone },
      { label: "Products", to: "/products", icon: Package },
    ],
  },
  {
    label: "Customers",
    items: [
      { label: "Retention", to: "/retention", icon: Repeat },
      { label: "Subscriptions", to: "/subscriptions", icon: RefreshCw },
    ],
  },
  ...NAV_GROUPS.slice(3),
];

export function navigationGroups(
  isOperator: boolean,
  financialBasis?: Capabilities["financialBasis"],
  subscriptionPlanState?: Capabilities["subscriptionPlanState"],
  subscriptionRecurringSales?: Capabilities["subscriptionRecurringSales"],
  subscriptionLifecycle?: Capabilities["subscriptionLifecycle"],
): NavGroup[] {
  const groups = isOperator ? NAV_GROUPS : CLIENT_NAV_GROUPS;
  return groups.map((group) => ({
    ...group,
    items: group.items.filter((item) =>
      (financialBasis === "profit" || item.to !== "/financials") &&
      (financialBasis === "profit" || isOperator || item.to !== "/costs") &&
      (isOperator || subscriptionPlanState === true || subscriptionRecurringSales === true || subscriptionLifecycle === true || item.to !== "/subscriptions"),
    ),
  }));
}

/** Connections is a setup surface, so unrelated number warnings do not belong
 * above its cards. Customer LTV now shares the single account-level notice. */
export function shouldShowGlobalClientNotices(pathname: string): boolean {
  return pathname !== "/connections";
}

export function clientEntryReady(state: Pick<OnboardingState, "completed" | "previewAllowed"> | undefined): boolean {
  return state?.completed === true || state?.previewAllowed === true;
}

export function appLayoutNoticePolicy(input: {
  pathname: string;
  entryReady: boolean;
  isOperator: boolean;
  financialBasis: Capabilities["financialBasis"] | undefined;
}): {
  grossResults: boolean;
  verification: boolean;
  costReadiness: boolean;
  dataTrust: boolean;
  needs: boolean;
  operatorCosts: boolean;
} {
  const globalSurface = input.entryReady && shouldShowGlobalClientNotices(input.pathname);
  // Profit/setup nags stay hidden until the server explicitly says the entire
  // account is on a profit basis. Revenue is the fail-closed display default,
  // so its one disclosure remains visible if capability loading fails.
  const profitPhase = input.financialBasis === "profit";
  return {
    grossResults: input.entryReady && !profitPhase && shouldShowGrossResultsNotice(input.pathname),
    verification: globalSurface && profitPhase,
    costReadiness: globalSurface && profitPhase,
    // Clients retain one revenue-mode explanation. Operators get a separate
    // actionable cost notice without changing any financial display gates.
    dataTrust: globalSurface && profitPhase,
    needs: globalSurface && profitPhase,
    operatorCosts: input.entryReady && input.isOperator && shouldShowGrossResultsNotice(input.pathname),
  };
}

// Page title per route — so the browser tab stops saying the same thing on every page.
const TITLES: Record<string, string> = {
  "/dashboard": "Exec", "/marketing": "Marketing", "/products": "Products",
  "/cohorts": "Customer LTV", "/retention": "Retention", "/subscriptions": "Subscriptions",
  "/operations": "Operations", "/financials": "Financials", "/connections": "Connections",
  "/settings": "Settings", "/costs": "Costs", "/welcome": "Get set up", "/admin": "Clients",
  "/costs/overhead": "Check business overhead",
  "/agency": "Agency portfolio", "/agency/feedback": "Feedback & fixes",
};

// Department pages whose cards are add/removable (wrapped in <Hideable> or
// MetricCard). The "Customize cards" toggle shows only here; Exec has its own
// drag/layout editor, and Connections/Settings have nothing to hide.
const CARD_CUSTOMIZE_ROUTES = new Set(["/marketing", "/products", "/cohorts", "/retention", "/subscriptions", "/operations", "/financials"]);
// Admin/config/onboarding pages don't scope by date, so the period bar +
// wholesale toggle hide there (founder 2026-07-01; onboarding added 2026-07-02).
const NO_PERIOD_ROUTES = ["/settings", "/connections", "/costs", "/costs/overhead", "/admin", "/welcome", "/onboarding/chat", "/agency", "/agency/feedback"];

export default function AppLayout(): React.ReactElement {
  const activeAccountId = useRequiredActiveAccountId();
  const { user } = useUser();
  const api = useApiClient();
  const { setColor } = useTheme();
  const location = useLocation();
  const navType = useNavigationType();
  const barRef = useRef<HTMLDivElement>(null);
  // "See it live →" deep-links from Agency › Cards land here with
  // ?spotlight=<renderId> — scroll to and flash that card.
  useCardSpotlight();

  // Collapse the left nav to a slim icon rail to reclaim screen width (founder
  // 2026-06-28: "collapse the sidebar on both sides"). Persisted.
  const [feedback, setFeedback] = useState<null | "bug" | "feature">(null);
  const vw = useViewportWidth();
  const [navCollapsed, setNavCollapsed] = useState<boolean>(() => {
    if (typeof window !== "undefined" && window.innerWidth < BREAK.nav) return true; // small screens start collapsed
    try { return localStorage.getItem("dd-nav-collapsed") === "1"; } catch { return false; }
  });
  const toggleNav = (): void => setNavCollapsed((c) => {
    const next = !c;
    try { localStorage.setItem("dd-nav-collapsed", next ? "1" : "0"); } catch { /* ignore */ }
    return next;
  });
  // Auto-collapse when the window gets narrow (founder 2026-07-05: "it would
  // collapse on its own, then they can expand it"). Only fires when CROSSING
  // the threshold — the user can still re-expand on a small screen, and their
  // saved wide-screen preference comes back when the window grows again.
  // Auto moves never touch the saved preference (only the toggle button does).
  const prevSmall = useRef(vw < BREAK.nav);
  useEffect(() => {
    const small = vw < BREAK.nav;
    if (small && !prevSmall.current) setNavCollapsed(true);
    if (!small && prevSmall.current) {
      try { setNavCollapsed(localStorage.getItem("dd-nav-collapsed") === "1"); } catch { setNavCollapsed(false); }
    }
    prevSmall.current = small;
  }, [vw]);
  // Content padding scales with the window — fixed 30/38px padding ate ~20% of
  // a phone's width. The sticky bar's negative margins must mirror it exactly.
  const padY = vw < 700 ? 14 : vw < 1000 ? 20 : 30;
  const padX = vw < 700 ? 14 : vw < 1000 ? 24 : 38;
  const responsiveMain: React.CSSProperties = { padding: `${padY}px ${padX}px` };
  const responsiveBar: React.CSSProperties = { margin: `-${padY}px -${padX}px 14px`, padding: `${padY}px ${padX}px 12px` };

  // Publish the sticky window-bar's live height as --sticky-bar-h so content
  // (e.g. the onboarding sheet's frozen group/column headers) can stick BELOW it
  // instead of hiding underneath. Tracks wraps/resizes.
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const set = (): void => document.documentElement.style.setProperty("--sticky-bar-h", `${Math.round(el.getBoundingClientRect().height)}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, [location.pathname]);

  // Crash reporter for the page ErrorBoundary: fire-and-forget POST into the
  // feedback pipeline (source 'ui_crash') so every render crash reaches the
  // agency console with the route + stacks attached — no user typing needed.
  // sessionStorage fingerprint: a broken render loop sends exactly ONE report
  // per session, not one per remount. Must never throw (crash screen safety).
  const reportCrash = (error: Error, info: { componentStack: string | null }): void => {
    try {
      const fingerprint = `${location.pathname}::${error.message}`.slice(0, 300);
      const key = "ccd.crashReported";
      if (window.sessionStorage.getItem(key) === fingerprint) return;
      window.sessionStorage.setItem(key, fingerprint);
      void api
        .post("/client-errors", {
          page: location.pathname,
          message: error.message || String(error),
          stack: error.stack ?? "",
          componentStack: info.componentStack ?? "",
        })
        .catch(() => undefined);
    } catch {
      /* best-effort — never let the reporter break the crash screen */
    }
  };

  const { data: onboarding } = useQuery({
    queryKey: ["onboarding", "state"],
    queryFn: () => api.get<OnboardingState>("/onboarding/state"),
  });

  const { data: capabilities } = useQuery({
    queryKey: ["capabilities"],
    queryFn: () => api.get<Capabilities>("/dashboard/capabilities"),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  // Agency operators get the Clients admin nav. Cheap server check; cache it.
  const { data: agency } = useQuery({
    queryKey: ["account", "agency-status"],
    queryFn: () => api.get<{ isOperator: boolean }>("/account/agency-status"),
    staleTime: 5 * 60_000,
  });
  const isOperator = agency?.isOperator ?? false;
  // Client-safe is the default while role lookup is loading or failed. An
  // operator gains admin links only after the server explicitly says so;
  // clients must never flash Costs or other operator-only setup surfaces.
  const clientLtvHome = agency?.isOperator !== true;
  const clientHomePath = clientLtvHome ? "/cohorts" : "/dashboard";
  const navGroups = navigationGroups(
    isOperator,
    capabilities?.financialBasis,
    capabilities?.subscriptionPlanState,
    capabilities?.subscriptionRecurringSales,
    capabilities?.subscriptionLifecycle,
  );

  // The account's own logo (white-label) shows in the top-left brand when set,
  // else the default mark + name (founder 2026-07-01). Shares the ["account"]
  // cache with Settings → Account, so uploading a logo there updates it here.
  const { data: acct } = useQuery({
    queryKey: ["account"],
    queryFn: () => api.get<{ account: { id: string; logoUrl: string | null; name: string; themeColorHex: string | null } }>("/account"),
    staleTime: 5 * 60_000,
  });
  const accountLogo = acct?.account.logoUrl && acct.account.logoUrl.length > 0 ? acct.account.logoUrl : null;

  // The primary brand color belongs to the account, not one browser. Everyone
  // sees the same brand after onboarding; Light/Dark remains personal.
  useEffect(() => {
    const hex = acct?.account.themeColorHex;
    if (hex && /^#[0-9a-fA-F]{6}$/.test(hex)) setColor("accent", hex);
  }, [acct?.account.themeColorHex, setColor]);

  // Onboarding's tab memory (sandbox flag, cached sandbox catalog, reviewed
  // marks) belongs to ONE client. When the active account changes (agency
  // switcher), wipe it so client A's products can never appear while working
  // as client B (founder 2026-07-02: Vital Roots run showed Everbella's
  // catalog). Account switches hard-reload the app, so a mount-time check
  // here covers every path into onboarding.
  useEffect(() => {
    const id = acct?.account.id;
    if (!id) return;
    const MARKER = "ccd.onboarding.account";
    try {
      if (window.sessionStorage.getItem(MARKER) !== id) {
        for (const k of Object.keys(window.sessionStorage)) {
          if (k.startsWith("ccd.onboarding.")) window.sessionStorage.removeItem(k);
        }
        window.sessionStorage.setItem(MARKER, id);
      }
    } catch { /* storage unavailable — nothing cached to leak */ }
  }, [acct?.account.id]);

  const required = onboarding?.steps.filter((s) => s.required) ?? [];
  const doneCount = required.filter((s) => s.complete).length;
  const entryReady = clientEntryReady(onboarding);
  // Wait for the role check before showing client-only setup language. This
  // prevents an agency operator from seeing a one-frame "Get set up" flash.
  const showClientSetup = onboarding !== undefined && !entryReady && agency?.isOperator === false;
  const operatorNotice = onboarding && !entryReady
    ? operatorSetupNotice({
        isOperator,
        completed: onboarding.completed,
        accountName: acct?.account.name,
        steps: onboarding.steps,
      })
    : null;
  const noticePolicy = appLayoutNoticePolicy({
    pathname: location.pathname,
    entryReady,
    isOperator,
    financialBasis: capabilities?.financialBasis,
  });

  useEffect(() => {
    const page = TITLES[location.pathname];
    document.title = page ? `${page} · Scale Advisors` : "Scale Advisors";
    // Land at the top on a fresh navigation — but don't fight back/forward scroll
    // restoration (POP) or in-page anchor links.
    if (!location.hash && navType !== "POP") window.scrollTo(0, 0);
  }, [location.pathname, location.hash, navType]);

  const navLinkStyle = ({ isActive }: { isActive: boolean }): React.CSSProperties => ({
    ...layoutStyles.navItem,
    ...(navCollapsed ? { justifyContent: "center", padding: "9px 0" } : {}),
    ...(isActive ? layoutStyles.navItemActive : {}),
  });
  // Icon style without the label gap when the rail is collapsed (icon-only).
  const navIcon = navCollapsed ? iconStyleCollapsed : iconStyle;

  return (
    <CardApprovalProvider>
    <PeriodProvider>
    <DisplayOptsProvider>
    <ToastProvider>
    <DrillProvider>
      <div style={layoutStyles.root}>
        <aside style={{ ...layoutStyles.sidebar, width: navCollapsed ? 66 : 240, padding: navCollapsed ? "22px 10px" : "22px 16px" }}>
          <div style={{ display: "flex", justifyContent: navCollapsed ? "center" : "flex-end", marginBottom: 8 }}>
            <button type="button" onClick={toggleNav} style={layoutStyles.navToggle}
              title={navCollapsed ? "Expand menu" : "Collapse menu"} aria-label={navCollapsed ? "Expand menu" : "Collapse menu"}>
              {navCollapsed ? <PanelLeftOpen size={18} strokeWidth={1.8} /> : <PanelLeftClose size={18} strokeWidth={1.8} />}
            </button>
          </div>
          <div style={{ ...layoutStyles.brand, marginBottom: navCollapsed ? 16 : 22 }}>
            <Link to={clientHomePath} style={layoutStyles.brandLink}>
              <span style={{ ...layoutStyles.brandRow, justifyContent: navCollapsed ? "center" : "flex-start" }}>
                {accountLogo ? (
                  <img src={accountLogo} alt={acct?.account.name ?? "Logo"} style={{ maxHeight: 30, maxWidth: navCollapsed ? 34 : 156, objectFit: "contain", display: "block" }} />
                ) : (
                  <>
                    <span style={layoutStyles.brandMark} aria-hidden="true" />
                    {!navCollapsed && <strong style={{ fontFamily: "var(--font-display)", fontSize: 16 }}>Scale Advisors</strong>}
                  </>
                )}
              </span>
            </Link>
            {/* Outside the Link — the switcher dropdown must not navigate. */}
            {!navCollapsed && <AccountSwitcher homePath={clientHomePath} />}
          </div>

          <nav style={layoutStyles.nav}>
            {/* Setup squats up top only while it's incomplete. */}
            {showClientSetup && (
              <NavLink to="/welcome" className="dd-nav-item" style={navLinkStyle} title={navCollapsed ? "Get set up" : undefined}>
                <Rocket size={17} strokeWidth={1.8} style={navIcon} />
                {!navCollapsed && <span style={{ flex: 1 }}>Get set up</span>}
                {!navCollapsed && required.length > 0 && <span style={layoutStyles.pill}>{doneCount}/{required.length}</span>}
              </NavLink>
            )}

            {navGroups.map((group) => (
              <div key={group.label ?? "top"} style={{ marginTop: group.label ? 14 : 0 }}>
                {group.label && !navCollapsed && <div style={layoutStyles.groupLabel}>{group.label}</div>}
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink key={item.to} to={item.to} className="dd-nav-item" style={navLinkStyle} title={navCollapsed ? item.label : undefined}>
                      <Icon size={17} strokeWidth={1.8} style={navIcon} />
                      {!navCollapsed && <span style={{ flex: 1 }}>{item.label}</span>}
                    </NavLink>
                  );
                })}
              </div>
            ))}

            {/* One door back to the operator's OWN app (founder 2026-07-05:
                the agency console is a separate shell now, not nav items
                squatting in the client sidebar). */}
            {isOperator && (
              <div style={{ marginTop: 14 }}>
                {!navCollapsed && <div style={layoutStyles.groupLabel}>Agency</div>}
                <NavLink to="/agency" end className="dd-nav-item" style={navLinkStyle} title={navCollapsed ? "Agency console" : undefined}>
                  <Briefcase size={17} strokeWidth={1.8} style={navIcon} />
                  {!navCollapsed && <span style={{ flex: 1 }}>Agency console</span>}
                </NavLink>
              </div>
            )}
          </nav>

          {/* Avatar + name, with the bug/feature actions folded in as small quiet
              icons (founder 2026-07-06: they were full nav rows and read as HUGE
              next to the real nav). In-app popup — no pop-out, popup blockers
              never apply. */}
          <div style={{ ...layoutStyles.userArea, ...(navCollapsed ? { flexDirection: "column", gap: 8, padding: "12px 0 4px" } : {}) }}>
            <UserButton afterSignOutUrl="/login" />
            {!navCollapsed && (
              <div style={layoutStyles.userName}>
                {user?.fullName ?? user?.primaryEmailAddress?.emailAddress}
              </div>
            )}
            <div style={{ display: "flex", gap: 2, ...(navCollapsed ? { flexDirection: "column" } : {}) }}>
              {[{ k: "bug" as const, Icon: Bug, label: "Submit a bug" }, { k: "feature" as const, Icon: Lightbulb, label: "Suggest a feature" }].map(({ k, Icon, label }) => (
                <button key={k} type="button" onClick={() => setFeedback(k)} className="dd-fbicon" title={label} aria-label={label}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 7, background: "transparent", border: "none", cursor: "pointer" }}>
                  <Icon size={15} strokeWidth={1.8} />
                </button>
              ))}
            </div>
          </div>
        </aside>

        {feedback && (
          <FeedbackModal
            kind={feedback}
            pathname={location.pathname}
            defaultName={user?.fullName ?? ""}
            defaultEmail={user?.primaryEmailAddress?.emailAddress ?? ""}
            onClose={() => setFeedback(null)}
          />
        )}

        {/* Page body = main content + (where applicable) the right filter rail,
            wrapped in one PageFiltersProvider so the rail and the cards share the
            same per-page filter. Keyed by the first path segment so it remounts
            (and re-applies that page's saved filter) on navigation. */}
        {(() => { const seg = location.pathname.split("/")[1] || "home"; const legacyLinked = capabilities?.subscriptionPlanState === undefined && capabilities?.subscriptionRecurringSales === undefined && capabilities?.subscriptionLifecycle === true; const hasRail = pageHasProductFilter(seg, capabilities?.subscriptionOrderLinkage === true || legacyLinked); return (
        <PageFiltersProvider key={`${activeAccountId}:${seg}:${hasRail ? "filters" : "no-filters"}`} accountId={activeAccountId} pageKey={seg} filtersEnabled={hasRail}>
        {/* When the filter rail is present, main fills the space up to it so the
            rail sits flush against the screen edge (founder 2026-06-28). */}
        <main style={{ ...layoutStyles.main, ...responsiveMain, ...(hasRail ? { maxWidth: "none" } : {}) }}>
          {/* One HideProvider per route wraps the whole page so the "Customize
              cards" toggle in the sticky bar shares context with the cards
              below. */}
          <HideProvider pageKey={seg}>
            {/* Window bar is STICKY (founder 2026-06-15: frozen on scroll).
                Negative margins bleed it to main's edges; its own padding
                restores the spacing so top:0 sticks it flush to the scrollport. */}
            <div ref={barRef} style={{ ...layoutStyles.stickyBar, ...responsiveBar }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                {/* Admin/config pages (Settings, Connections, Clients, onboarding)
                    don't scope by date or product, so the date range + wholesale
                    toggle are hidden there (founder 2026-07-01). Revenue adjustments
                    already only live in the product rail, which is off on these. */}
                {!NO_PERIOD_ROUTES.includes(location.pathname) && <PeriodBar />}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {/* "Include wholesale" now lives in the filter sidebar's Revenue
                      adjustments group (founder 2026-07-03), next to the other
                      revenue toggles — removed from the top bar to match. */}
                  <SyncPill />
                  {/* Customize design (theme) + Customize cards (add/remove),
                      side by side (founder 2026-06-15). Cards toggle only on the
                      pages that have hideable cards. */}
                  {CARD_CUSTOMIZE_ROUTES.has(location.pathname) && <CustomizeBar />}
                  {/* "Customize design" is dashboard chrome — hidden on setup/
                      admin pages (founder 2026-07-03: takes up space during
                      onboarding). */}
                  {!NO_PERIOD_ROUTES.includes(location.pathname) && <CustomizePanel />}
                </div>
              </div>
            </div>
            {showClientSetup && shouldShowGlobalClientNotices(location.pathname) && (
              <Link to="/welcome" style={layoutStyles.banner}>
                <span>Finish setup to see your numbers.</span>
                <span style={layoutStyles.bannerCta}>Resume setup →</span>
              </Link>
            )}
            {operatorNotice && (
              <Link to={operatorNotice.href} style={layoutStyles.banner}>
                <span>{operatorNotice.message}</span>
                <span style={layoutStyles.bannerCta}>{operatorNotice.action}</span>
              </Link>
            )}
            {noticePolicy.grossResults && <GrossResultsNotice accountId={acct?.account.id} />}
            {noticePolicy.operatorCosts && <OperatorCostNotice accountId={activeAccountId} />}
            {noticePolicy.verification && <VerificationReadinessBanner />}
            {noticePolicy.costReadiness && <CostReadinessBanner />}
            {/* Source-specific locks remain on affected results in the gross
                phase. The large diagnostic stack stays operator/profit-only so
                clients see one account-level notice, not a warning wall. */}
            {noticePolicy.dataTrust && <DataTrustBanner />}
            {noticePolicy.needs && <NeedsBanner />}
            {/* Keyed by PATH, not location.key: search-param writes (onboarding's
                ?stage=, ?mode=sandbox) must not remount the whole page — keying
                on location.key made every param write restart onboarding. */}
            <ErrorBoundary key={location.pathname} onError={reportCrash}>
              <Outlet />
            </ErrorBoundary>
            {location.pathname === "/dashboard" && <DashboardFirstInsightTour />}
            {/* Stale-tab killer: detects new deploys, banners + refreshes safely. */}
            <VersionWatcher />
          </HideProvider>
        </main>
        {hasRail && <PageFilterRail />}
        </PageFiltersProvider>
        ); })()}
      </div>
    </DrillProvider>
    </ToastProvider>
    </DisplayOptsProvider>
    </PeriodProvider>
    </CardApprovalProvider>
  );
}

const iconStyle: React.CSSProperties = { marginRight: 10, flexShrink: 0, verticalAlign: "-3px" };
// Icon style for the collapsed (icon-only) rail — no label, so no right gap.
const iconStyleCollapsed: React.CSSProperties = { flexShrink: 0, verticalAlign: "-3px" };

const switcherStyles: Record<string, React.CSSProperties> = {
  trigger: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    maxWidth: 180,
    background: "transparent",
    border: "none",
    padding: 0,
    fontSize: 12,
    fontWeight: 600,
    color: "var(--text-dim)",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  overlay: { position: "fixed", inset: 0, zIndex: 40 },
  menu: {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: -8,
    zIndex: 41,
    minWidth: 210,
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "var(--r-sm)",
    boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
    padding: 6,
  },
  menuLabel: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "var(--text-faint)",
    padding: "6px 10px 4px",
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    width: "100%",
    background: "transparent",
    border: "none",
    borderRadius: 6,
    padding: "8px 10px",
    fontSize: 13,
    fontWeight: 500,
    color: "var(--text)",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  itemActive: {
    background: "color-mix(in srgb, var(--accent) 12%, transparent)",
    fontWeight: 600,
  },
  swatch: { width: 10, height: 10, borderRadius: 3, flexShrink: 0 },
};

const layoutStyles: Record<string, React.CSSProperties> = {
  root: {
    display: "flex",
    minHeight: "100vh",
    background: "var(--bg)",
    color: "var(--text)",
    fontFamily: "var(--font-body)",
  },
  sidebar: {
    width: 240,
    background: "var(--surface)",
    borderRight: "1px solid var(--border)",
    display: "flex",
    flexDirection: "column",
    padding: "22px 16px",
    position: "sticky",
    top: 0,
    height: "100vh",
  },
  navToggle: {
    border: "none", background: "transparent", color: "var(--text-faint)", cursor: "pointer",
    padding: 5, display: "flex", alignItems: "center", borderRadius: 7,
  },
  brand: { marginBottom: 22 },
  brandLink: { color: "var(--text)", textDecoration: "none", display: "block" },
  brandRow: { display: "flex", alignItems: "center", gap: 9 },
  brandMark: {
    width: 18,
    height: 18,
    borderRadius: 5,
    background: "var(--accent)",
    boxShadow: "0 0 0 3px color-mix(in srgb, var(--accent) 22%, transparent)",
    display: "inline-block",
  },
  subBrand: { fontSize: 12, color: "var(--text-faint)", marginTop: 5, marginLeft: 27 },
  nav: { flex: 1, display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" },
  groupLabel: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "var(--text-faint)",
    padding: "0 12px",
    marginBottom: 5,
  },
  navItem: {
    // color is set via the .dd-nav-item CSS class (not inline) so :hover can
    // override it; active state re-sets color inline below.
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    borderRadius: "var(--r-sm)",
    textDecoration: "none",
    fontSize: 13.5,
    fontWeight: 500,
  },
  navItemActive: {
    background: "color-mix(in srgb, var(--accent) 14%, transparent)",
    color: "var(--text)",
    fontWeight: 600,
  },
  pill: {
    fontSize: 11,
    fontWeight: 700,
    color: "var(--accent-contrast)",
    background: "var(--accent)",
    padding: "1px 8px",
    borderRadius: 10,
  },
  userArea: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 8px 4px",
    borderTop: "1px solid var(--border)",
    marginTop: 12,
  },
  userName: {
    flex: 1,
    fontSize: 12,
    color: "var(--text-dim)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  // No overflowY:auto here — the DOCUMENT is the scroll container (the sidebar
  // is position:sticky). An inner overflow:auto made `main` a scroll context
  // that never actually scrolls, which BROKE the sticky window bar (it stuck to
  // main, but main rode up with the page). Letting the document scroll lets the
  // window bar stick to the viewport. minWidth:0 keeps flex children from
  // overflowing.
  main: { flex: 1, minWidth: 0, padding: "30px 38px", maxWidth: 1280 },
  stickyBar: {
    position: "sticky", top: 0, zIndex: 20,
    background: "var(--bg)",
    margin: "-30px -38px 14px", padding: "30px 38px 12px",
    borderBottom: "1px solid var(--border)",
  },
  banner: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "color-mix(in srgb, var(--c2) 14%, var(--surface))",
    border: "1px solid color-mix(in srgb, var(--c2) 40%, var(--border))",
    color: "var(--text)",
    padding: "10px 16px",
    borderRadius: "var(--r-sm)",
    fontSize: 13,
    marginBottom: 24,
    textDecoration: "none",
  },
  bannerCta: { fontWeight: 600, color: "var(--accent)" },
};
